import { Link } from "@tanstack/react-router";
import { useEffect, useRef, useState } from "react";
import { z } from "zod";
import { PageShell, PageHero } from "@project/user/components/page-shell";
import { Button } from "@project/components/ui/button";
import { Input } from "@project/components/ui/input";
import { Label } from "@project/components/ui/label";
import { Textarea } from "@project/components/ui/textarea";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@project/components/ui/select";
import { ArrowLeft, CheckCircle2, Upload, X, Loader2 } from "lucide-react";
import { toast } from "sonner";
import { HERO_IMAGES } from "@project/lib/stock-images";
import { useI18n } from "@project/lib/use-i18n";
import {
  publicItemCategoryService,
  publicItemDonationService,
} from "@project/services/donate";
import { MobileNumberField } from "@project/components/mobile-number-field";
import {
  DEFAULT_PHONE_COUNTRY,
  isValidMobileNumber,
  type PhoneCountryCode,
} from "@project/lib/mobile-number";

const schema = z.object({
  full_name: z.string().trim().min(2, "Full name is required").max(100),
  mobile_number: z.string().trim().min(1, "Mobile number is required"),
  email: z
    .string()
    .trim()
    .email("Invalid email")
    .max(255)
    .optional()
    .or(z.literal("")),
  item_category: z.string().min(1, "Please select a category"),
  item_name: z.string().trim().min(2, "Item name is required").max(200),
  quantity: z.coerce.number().min(1, "Quantity must be at least 1"),
  pickup_address: z
    .string()
    .trim()
    .min(5, "Pickup address is required")
    .max(500),
  area: z.string().trim().min(2, "Area is required").max(100),
  landmark: z.string().max(200).optional().or(z.literal("")),
  pincode: z.string().trim().min(6, "Valid pincode required").max(10),
  message: z.string().max(1000).optional().or(z.literal("")),
});

export default function ScrapDonationForm() {
  const { t } = useI18n();
  const [submitting, setSubmitting] = useState(false);
  const [done, setDone] = useState(false);
  const [categories, setCategories] = useState<{ id: number; name: string }[]>(
    []
  );
  const [photo, setPhoto] = useState<File | null>(null);
  const [photoPreview, setPhotoPreview] = useState<string | null>(null);
  const fileRef = useRef<HTMLInputElement>(null);
  const [form, setForm] = useState({
    full_name: "",
    mobile_number: "",
    mobile_country: DEFAULT_PHONE_COUNTRY as PhoneCountryCode,
    email: "",
    item_category: "",
    item_name: "",
    quantity: "1",
    pickup_address: "",
    area: "",
    landmark: "",
    pincode: "",
    message: "",
  });

  const set = (k: keyof typeof form, v: string) =>
    setForm(p => ({ ...p, [k]: v }));

  useEffect(() => {
    let ignore = false;
    void publicItemCategoryService
      .listActiveByType("scrap", { page_size: 100 })
      .then(res => {
        if (!ignore) {
          setCategories(res.data.map(c => ({ id: c.id, name: c.name })));
        }
      })
      .catch(() => {
        if (!ignore) {
          setCategories([]);
        }
      });
    return () => {
      ignore = true;
    };
  }, []);

  const onPhotoSelect = (file: File | undefined) => {
    if (!file) {
      return;
    }
    if (file.size > 5 * 1024 * 1024) {
      return toast.error("Photo must be under 5 MB");
    }
    if (photoPreview) {
      URL.revokeObjectURL(photoPreview);
    }
    setPhoto(file);
    setPhotoPreview(URL.createObjectURL(file));
  };

  const clearPhoto = () => {
    if (photoPreview) {
      URL.revokeObjectURL(photoPreview);
    }
    setPhoto(null);
    setPhotoPreview(null);
    if (fileRef.current) {
      fileRef.current.value = "";
    }
  };

  const submit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!isValidMobileNumber(form.mobile_number, form.mobile_country)) {
      return toast.error(
        "Enter a valid mobile number for the selected country"
      );
    }
    const parsed = schema.safeParse(form);
    if (!parsed.success) {
      return toast.error(
        parsed.error.issues[0]?.message ?? "Please check the form"
      );
    }
    setSubmitting(true);
    try {
      const result = await publicItemDonationService.submitScrap({
        full_name: parsed.data.full_name,
        mobile_number: parsed.data.mobile_number,
        email: parsed.data.email ?? undefined,
        item_category: Number(parsed.data.item_category),
        item_name: parsed.data.item_name,
        quantity: parsed.data.quantity,
        item_condition: "scrap",
        pickup_address: parsed.data.pickup_address,
        area: parsed.data.area,
        landmark: parsed.data.landmark ?? undefined,
        pincode: parsed.data.pincode,
        message: parsed.data.message ?? undefined,
        photo,
      });
      if (result === undefined) {
        throw new Error("Could not submit scrap donation");
      }
      setDone(true);
    } catch (err: unknown) {
      const message =
        err instanceof Error ? err.message : "Could not submit scrap donation";
      toast.error(message);
    } finally {
      setSubmitting(false);
    }
  };

  if (done) {
    return (
      <PageShell>
        <div className="mx-auto max-w-xl px-4 py-16 text-center">
          <div
            className="bg-gold/15 anim-zoom-in mx-auto grid h-20 w-20 place-items-center rounded-full text-gold"
            style={{ animationDelay: "120ms" }}
          >
            <CheckCircle2 className="h-12 w-12" />
          </div>
          <h1
            className="anim-fade-up mt-6 font-serif text-3xl font-bold text-primary"
            style={{ animationDelay: "260ms" }}
          >
            {t("pages.scrapDonation.successTitle")}
          </h1>
          <p
            className="anim-fade-up mt-3 text-muted-foreground"
            style={{ animationDelay: "380ms" }}
          >
            {t("pages.scrapDonation.successDesc")}
          </p>
          <div
            className="anim-fade-up mt-6 flex justify-center gap-3"
            style={{ animationDelay: "500ms" }}
          >
            <Button asChild>
              <Link to="/">{t("account.visitorSuccessDialog.backToHome")}</Link>
            </Button>
            <Button asChild variant="outline">
              <Link to="/donate">{t("pages.scrapDonation.browseCauses")}</Link>
            </Button>
          </div>
        </div>
      </PageShell>
    );
  }

  return (
    <PageShell>
      <PageHero
        eyebrow={t("pages.scrapDonation.hero.eyebrow")}
        title={t("pages.scrapDonation.hero.title")}
        subtitle={t("pages.scrapDonation.hero.subtitle")}
        image={HERO_IMAGES.scrap}
      />
      <section className="mx-auto max-w-4xl px-4 py-8 sm:py-12">
        <Button
          asChild
          variant="ghost"
          className="-ml-3 mb-6 text-muted-foreground hover:text-primary"
        >
          <Link to="/donate/items">
            <ArrowLeft className="mr-1.5 h-4 w-4" /> {t("pages.scrapDonation.backLink")}
          </Link>
        </Button>
        <div className="anim-fade-up" style={{ animationDelay: "600ms" }}>
          <form onSubmit={submit}>
            <div className="space-y-5 rounded-2xl border border-border bg-card p-6 shadow-sm">
              <h2 className="font-serif text-xl font-bold text-primary">
                {t("pages.scrapDonation.sections.yourDetails")}
              </h2>
              <div className="grid gap-3 md:grid-cols-2">
                <div className="md:col-span-2">
                  <Label>{t("pages.scrapDonation.labels.fullName")}</Label>
                  <Input
                    required
                    value={form.full_name}
                    onChange={e => set("full_name", e.target.value)}
                  />
                </div>
                <div>
                  <Label>{t("pages.scrapDonation.labels.mobile")}</Label>
                  <MobileNumberField
                    value={form.mobile_number}
                    country={form.mobile_country}
                    onCountryChange={code =>
                      setForm(p => ({ ...p, mobile_country: code }))
                    }
                    onChange={value => set("mobile_number", value)}
                  />
                </div>
                <div>
                  <Label>{t("pages.scrapDonation.labels.email")}</Label>
                  <Input
                    type="email"
                    value={form.email}
                    onChange={e => set("email", e.target.value)}
                  />
                </div>
              </div>

              <div className="border-t border-border pt-4">
                <h2 className="font-serif text-xl font-bold text-primary">
                  {t("pages.scrapDonation.sections.whatToDonate")}
                </h2>
                <div className="mt-3 grid gap-3 md:grid-cols-2">
                  <div>
                    <Label data-required="true">{t("pages.scrapDonation.labels.category")}</Label>
                    <Select
                      value={form.item_category}
                      onValueChange={v => set("item_category", v)}
                    >
                      <SelectTrigger>
                        <SelectValue placeholder={t("pages.scrapDonation.labels.selectCategory")} />
                      </SelectTrigger>
                      <SelectContent>
                        {categories.map(c => (
                          <SelectItem key={c.id} value={String(c.id)}>
                            {c.name}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                  <div>
                    <Label>{t("pages.scrapDonation.labels.quantity")}</Label>
                    <Input
                      required
                      type="number"
                      min={1}
                      value={form.quantity}
                      onChange={e => set("quantity", e.target.value)}
                      placeholder={t("pages.scrapDonation.labels.quantityPlaceholder")}
                    />
                  </div>
                  <div className="md:col-span-2">
                    <Label>{t("pages.scrapDonation.labels.itemName")}</Label>
                    <Input
                      required
                      value={form.item_name}
                      onChange={e => set("item_name", e.target.value)}
                      placeholder={t("pages.scrapDonation.labels.itemNamePlaceholder")}
                    />
                  </div>
                </div>
              </div>

              <div className="border-t border-border pt-4">
                <h2 className="font-serif text-xl font-bold text-primary">
                  {t("pages.scrapDonation.sections.pickupDetails")}
                </h2>
                <div className="mt-3 grid gap-3">
                  <div>
                    <Label>{t("pages.scrapDonation.labels.pickupAddress")}</Label>
                    <Textarea
                      required
                      rows={2}
                      value={form.pickup_address}
                      onChange={e => set("pickup_address", e.target.value)}
                      placeholder={t("pages.scrapDonation.labels.pickupAddressPlaceholder")}
                    />
                  </div>
                  <div className="grid gap-3 md:grid-cols-2">
                    <div>
                      <Label>{t("pages.scrapDonation.labels.area")}</Label>
                      <Input
                        required
                        value={form.area}
                        onChange={e => set("area", e.target.value)}
                        placeholder={t("pages.scrapDonation.labels.areaPlaceholder")}
                      />
                    </div>
                    <div>
                      <Label>{t("pages.scrapDonation.labels.landmark")}</Label>
                      <Input
                        value={form.landmark}
                        onChange={e => set("landmark", e.target.value)}
                        placeholder={t("pages.scrapDonation.labels.landmarkPlaceholder")}
                      />
                    </div>
                  </div>
                  <div className="md:w-1/2">
                    <Label>{t("pages.scrapDonation.labels.pincode")}</Label>
                    <Input
                      required
                      value={form.pincode}
                      onChange={e => set("pincode", e.target.value)}
                      placeholder={t("pages.scrapDonation.labels.pincodePlaceholder")}
                    />
                  </div>
                </div>
              </div>

              <div className="border-t border-border pt-4">
                <Label>{t("pages.scrapDonation.sections.itemPhoto")}</Label>
                <p className="mb-2 text-xs text-muted-foreground">
                  {t("pages.scrapDonation.labels.photoHelp")}
                </p>
                <div className="flex items-center gap-3">
                  <div className="bg-muted/40 grid h-24 w-24 shrink-0 place-items-center overflow-hidden rounded-xl border-2 border-dashed border-border">
                    {photoPreview ? (
                      <img
                        src={photoPreview}
                        alt="Item preview"
                        className="h-full w-full object-cover"
                      />
                    ) : (
                      <Upload className="h-5 w-5 text-muted-foreground" />
                    )}
                  </div>
                  <div className="flex flex-col gap-1">
                    <input
                      ref={fileRef}
                      type="file"
                      accept="image/*"
                      className="hidden"
                      onChange={e => onPhotoSelect(e.target.files?.[0])}
                    />
                    <Button
                      type="button"
                      size="sm"
                      variant="outline"
                      onClick={() => fileRef.current?.click()}
                    >
                      <Upload className="h-3 w-3" />{" "}
                      {photo ? t("pages.scrapDonation.labels.changePhoto") : t("pages.scrapDonation.labels.uploadPhoto")}
                    </Button>
                    {photo && (
                      <Button
                        type="button"
                        size="sm"
                        variant="ghost"
                        className="text-destructive"
                        onClick={clearPhoto}
                      >
                        <X className="h-3 w-3" /> {t("pages.scrapDonation.labels.removePhoto")}
                      </Button>
                    )}
                  </div>
                </div>
              </div>

              <div>
                <Label>{t("pages.scrapDonation.labels.message")}</Label>
                <Textarea
                  rows={3}
                  value={form.message}
                  onChange={e => set("message", e.target.value)}
                  placeholder={t("pages.scrapDonation.labels.messagePlaceholder")}
                />
              </div>

              <Button
                type="submit"
                size="lg"
                disabled={submitting}
                className="hover:bg-gold/90 w-full bg-gold text-gold-foreground"
              >
                {submitting ? (
                  <>
                    <Loader2 className="mr-2 h-4 w-4 animate-spin" />{" "}
                    {t("pages.scrapDonation.labels.submitting")}
                  </>
                ) : (
                  t("pages.scrapDonation.labels.submit")
                )}
              </Button>
            </div>
          </form>
        </div>
      </section>
    </PageShell>
  );
}
